import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Bug,
  Calendar,
  CheckCircle,
  Coins,
  Crown,
  History,
  Mail,
  Receipt,
  TreePalm,
  XCircle
} from 'lucide-react';
import { useAuth } from '../hooks/use-auth';
import {
  getDiseaseHistory,
  getUserData,
  getUserHistory
} from '../lib/firestore';
import { useEffect, useState } from 'react';
import { PlantHistoryModal } from '../components/plant/PlantHistoryModal';
import { PlantHistoryItem } from '../components/plant/PlantHistoryItem';
import { LoadingSpinner } from '../components/loading/LoadingSpinner';
import { MetaTags } from '../components/seo/MetaTags';
import { Link } from 'react-router-dom';
import {
  getPurchaseHistory,
  getUserCredits,
  purchaseCredits
} from '../lib/credits';
import { DiseaseHistoryModal } from '../components/disease/DiseaseHistoryModal';
import { DiseaseHistoryItem } from '../components/disease/DiseaseHistoryItem';
import { CreditPurchaseModal } from '../components/credits/CreditPurchaseModal';
import { useToast } from '../hooks/use-toast';

function PurchaseHistoryItem({ purchase }) {
  // Format date safely with a fallback
  const formatDate = timestamp => {
    if (!timestamp) return 'N/A';
    // Handle both Firestore Timestamps and regular Dates
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg border p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Receipt className="w-4 h-4 text-emerald-600" />
            <span className="font-medium text-gray-900">
              {purchase.amount} Credits
            </span>
          </div>
          <div className="mt-1 text-sm text-gray-600">
            ₹{purchase.cost} • {formatDate(purchase.timestamp)}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="w-4 h-4 text-amber-500" />
          <span className="text-amber-700">
            {purchase.type === 'premium'
              ? 'Premium Credits (Never Expire)'
              : `Expires: ${formatDate(purchase.expiresAt)}`}
          </span>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2">
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            purchase.status === 'completed'
              ? 'bg-green-100 text-green-800'
              : 'bg-yellow-100 text-yellow-800'
          }`}
        >
          {purchase.status === 'completed' ? (
            <>
              <CheckCircle className="w-3 h-3 mr-1" />
              Completed
            </>
          ) : (
            'Processing'
          )}
        </span>
        {purchase.type === 'premium' && (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            Premium
          </span>
        )}
      </div>
    </motion.div>
  );
}

export function Profile() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [userData, setUserData] = useState(null);
  const [history, setHistory] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [diseaseHistory, setDiseaseHistory] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [isDiseaseModalOpen, setIsDiseaseModalOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  useEffect(() => {
    async function fetchData() {
      if (user) {
        try {
          const [
            data,
            plantHistory,
            userCredits,
            creditHistory,
            diseaseDetections
          ] = await Promise.all([
            getUserData(user.uid),
            getUserHistory(user.uid),
            getUserCredits(user.uid),
            getPurchaseHistory(user.uid),
            getDiseaseHistory(user.uid)
          ]);
          setUserData(data);
          setHistory(plantHistory);
          setCredits(userCredits);
          setPurchaseHistory(creditHistory);
          setDiseaseHistory(diseaseDetections);
        } catch (error) {
          console.error('Error fetching data:', error);
          toast({
            title: 'Error',
            description: 'Failed to load profile data',
            variant: 'destructive'
          });
        } finally {
          setIsLoading(false);
        }
      }
    }

    fetchData();
  }, [user, toast]);

  const handlePlantClick = plant => {
    setSelectedPlant(plant);
    setIsModalOpen(true);
  };

  const isLowCredits = credits <= 2;
  const noCredits = credits <= 0;

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  const handlePurchaseCredits = async amount => {
    try {
      await purchaseCredits(user.uid, amount);
      const newBalance = await getUserCredits(user.uid);
      setCredits(newBalance);

      toast({
        title: 'Success',
        description: `Successfully purchased ${amount} credits!`,
        variant: 'default'
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to purchase credits',
        variant: 'destructive'
      });
      throw error;
    }
  };

  return (
    <>
      <MetaTags
        title="Profile"
        description="Manage your Plant Identifier profile and settings."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 px-4 pb-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-2xl mx-auto"
        >
          {/* User Info Card */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center">
                {userData?.photoURL ? (
                  <motion.img
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    src={userData?.photoURL}
                    className="w-16 h-16 rounded-full"
                  />
                ) : (
                  <TreePalm className="w-8 h-8 text-emerald-600" />
                )}
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {userData?.name}
                </h1>
                <p className="text-gray-600">{user?.email}</p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Credits and Tier Section */}
              <div className="grid md:grid-cols-2 gap-4">
                <div
                  className={`rounded-lg p-4 ${
                    noCredits
                      ? 'bg-red-50'
                      : isLowCredits
                        ? 'bg-amber-50'
                        : 'bg-gradient-to-br from-emerald-50 to-emerald-100/50'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    {noCredits ? (
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                    ) : isLowCredits ? (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    ) : (
                      <Coins className="w-5 h-5 text-emerald-600" />
                    )}
                    <h3
                      className={`font-semibold ${
                        noCredits
                          ? 'text-red-800'
                          : isLowCredits
                            ? 'text-amber-800'
                            : 'text-gray-900'
                      }`}
                    >
                      Credits
                    </h3>
                  </div>

                  <div className="space-y-2">
                    {/* Regular Credits */}
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-gray-600">Monthly:</span>
                      <span
                        className={`text-xl font-bold ${
                          credits?.regular === 0
                            ? 'text-red-600'
                            : credits?.regular <= 2
                              ? 'text-amber-600'
                              : 'text-emerald-600'
                        }`}
                      >
                        {credits?.regular || 0}
                      </span>
                      <span className="text-sm text-gray-500">credits</span>
                    </div>

                    {/* Premium Credits */}
                    <div className="flex items-baseline gap-1">
                      <span className="text-sm text-gray-600">Premium:</span>
                      <span className="text-xl font-bold text-purple-600">
                        {credits?.premium || 0}
                      </span>
                      <span className="text-sm text-gray-500">credits</span>
                    </div>

                    {/* Total Credits */}
                    <div className="border-t border-gray-200 pt-2 mt-2">
                      <div className="flex items-baseline gap-1">
                        <span className="text-sm font-medium text-gray-700">
                          Total Available:
                        </span>
                        <span
                          className={`text-2xl font-bold ${
                            credits?.total === 0
                              ? 'text-red-600'
                              : credits?.total <= 2
                                ? 'text-amber-600'
                                : 'text-emerald-600'
                          }`}
                        >
                          {credits?.total || 0}
                        </span>
                        <span className="text-sm text-gray-500">credits</span>
                      </div>
                    </div>
                  </div>

                  {noCredits && (
                    <p className="text-sm text-red-600 mt-2">
                      Your credits are exhausted. Purchase more credits to
                      continue identifying plants.
                    </p>
                  )}
                  {isLowCredits && !noCredits && (
                    <p className="text-sm text-amber-600 mt-2">
                      Running low on credits. Consider purchasing more to
                      continue using the service.
                    </p>
                  )}
                  <button
                    onClick={() => setIsPurchaseModalOpen(true)}
                    className={`inline-block mt-3 text-sm font-medium px-4 py-2 rounded-lg ${
                      noCredits
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : isLowCredits
                          ? 'bg-amber-600 text-white hover:bg-amber-700'
                          : 'bg-emerald-600 text-white hover:bg-emerald-700'
                    } transition-colors`}
                  >
                    Purchase Credits
                  </button>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100/50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className="w-5 h-5 text-amber-600" />
                    <h3 className="font-semibold text-gray-900">
                      Account Tier
                    </h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-semibold text-amber-600 capitalize">
                      {userData?.userTier || 'Free'}
                    </span>
                    <span className="text-sm bg-amber-200/50 text-amber-700 px-2 py-0.5 rounded">
                      5 identifications/month
                    </span>
                  </div>
                  <Link
                    to="/pricing"
                    className="mt-2 text-sm text-amber-600 hover:text-amber-700 font-medium"
                  >
                    Upgrade to Pro →
                  </Link>
                </div>
              </div>

              {/* Rest of the component remains the same */}
              {/* Account Details Section */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h2 className="font-semibold text-gray-900 mb-2">
                  Account Details
                </h2>
                <div className="space-y-2">
                  <p className="text-sm text-gray-600">
                    Member since:{' '}
                    {new Date(userData?.createdAt).toLocaleDateString()}
                  </p>
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">Email verification status:</span>
                    {user?.emailVerified ? (
                      <div className="flex items-center text-green-600">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        <span className="text-sm">Verified</span>
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <XCircle className="w-4 h-4 mr-1" />
                        <span className="text-sm">Not verified</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-4">
              <History className="w-5 h-5 text-emerald-600" />
              <h2 className="text-xl font-semibold">
                Plant Identification History
              </h2>
            </div>

            {history.length === 0 ? (
              <div className="text-center text-gray-600 py-8">
                <TreePalm className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No plant identifications yet</p>
                <p className="text-sm">
                  Start identifying plants to see your history here!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {history.map((item, index) => (
                  <PlantHistoryItem
                    key={index}
                    item={item}
                    onClick={handlePlantClick}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <div className="flex items-center gap-3 mb-6">
              <Bug className="w-5 h-5 text-amber-600" />
              <h2 className="text-xl font-semibold">
                Disease Detection History
              </h2>
            </div>

            {diseaseHistory.length === 0 ? (
              <div className="text-center text-gray-600 py-8">
                <Bug className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No disease detections yet</p>
                <Link
                  to="/disease-detection"
                  className="text-amber-600 hover:text-amber-700 font-medium mt-2 inline-block"
                >
                  Start detecting diseases
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {diseaseHistory.map(detection => (
                  <DiseaseHistoryItem
                    key={detection.id}
                    detection={detection}
                    onClick={detection => {
                      setSelectedDisease(detection);
                      setIsDiseaseModalOpen(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Credit Purchase History */}
          <div className="bg-white rounded-lg shadow-lg p-6">
            <div className="flex items-center gap-3 mb-6">
              <Receipt className="w-5 h-5 text-emerald-600" />
              <h2 className="text-xl font-semibold">Credit Purchase History</h2>
            </div>

            {purchaseHistory.length === 0 ? (
              <div className="text-center text-gray-600 py-8">
                <Receipt className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>No purchase history</p>
                <p className="text-sm">
                  Your credit purchase history will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {purchaseHistory.map(purchase => (
                  <PurchaseHistoryItem key={purchase.id} purchase={purchase} />
                ))}
              </div>
            )}
          </div>
        </motion.div>

        <PlantHistoryModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          plant={selectedPlant}
        />

        {/* Disease History Modal */}
        <DiseaseHistoryModal
          isOpen={isDiseaseModalOpen}
          onClose={() => setIsDiseaseModalOpen(false)}
          detection={selectedDisease}
        />

        {/* Credit Purchase Modal */}
        <CreditPurchaseModal
          isOpen={isPurchaseModalOpen}
          onClose={() => setIsPurchaseModalOpen(false)}
          onPurchase={handlePurchaseCredits}
        />
      </div>
    </>
  );
}
